import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Send, 
  Phone, 
  ArrowLeft, 
  Loader2,
  CheckCircle2,
  CreditCard,
  User,
  Mail,
  Sparkles,
  MessageSquare,
  Lightbulb,
  Wand2,
  RefreshCw,
  Check,
  Star,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

const AIInterviewPage = () => {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  const [isPaying, setIsPaying] = useState(false);
  const [interviewPaid, setInterviewPaid] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isImproving, setIsImproving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const bldrPlans = [
    { id: 'starter', name: 'BLDR Starter', price: 99, features: ['Business Formation', 'EIN Registration', 'Basic Templates'] },
    { id: 'pro', name: 'BLDR Pro', price: 199, features: ['Everything in Starter', 'Financial Setup', 'Marketing Foundation'], popular: true },
    { id: 'elite', name: 'BLDR Elite', price: 299, features: ['Everything in Pro', 'Legal Package', 'CFO Advisory'] },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInterviewPayment = async () => {
    setIsPaying(true);
    setTimeout(() => {
      setIsPaying(false);
      setInterviewPaid(true);
      toast.success('Payment successful! Starting Interview Lite...');
      startInterview();
    }, 1500);
  };

  const startInterview = async () => {
    setIsStarting(true);
    try {
      const response = await axios.post(`${API}/interview/start`, {});
      setSessionId(response.data.session_id);
      setMessages([{ role: 'assistant', content: response.data.message }]);
    } catch (error) {
      console.error('Error starting interview:', error);
      toast.error('Failed to start interview. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setAiSuggestions([]);
    setShowSuggestions(false);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/interview/message`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
      
      if (response.data.is_complete) {
        setIsComplete(true);
        setTimeout(() => setShowContactModal(true), 1500);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getAiSuggestions = async () => {
    if (isLoading) return;
    
    setShowSuggestions(true);
    try {
      const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
      const context = lastAssistantMsg?.content || '';
      
      const response = await axios.post(`${API}/ai/suggest`, {
        field: 'interview_response',
        context: context
      });
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      setAiSuggestions([
        "Focus on your unique value proposition",
        "Share specific examples and numbers",
        "Explain your passion and motivation"
      ]);
    }
  };

  const improveMyText = async () => {
    if (!inputMessage.trim() || isImproving) return;
    
    setIsImproving(true);
    try {
      const response = await axios.post(`${API}/ai/improve-text`, {
        text: inputMessage,
        field: 'interview_response'
      });
      
      if (response.data.improved_text) {
        setInputMessage(response.data.improved_text);
        toast.success('Text improved! Review and send when ready.');
      }
    } catch (error) {
      toast.error('Could not improve text. Please try again.');
    } finally {
      setIsImproving(false);
    }
  };

  const handleContactSubmit = async () => {
    if (!contactInfo.name || !contactInfo.email) {
      toast.error('Please provide your name and email');
      return;
    }

    try {
      await axios.post(`${API}/interview/complete`, {
        session_id: sessionId,
        client_name: contactInfo.name,
        client_email: contactInfo.email,
        client_phone: contactInfo.phone
      });
      setShowContactModal(false);
      setShowUpgradeModal(true);
    } catch (error) {
      console.error('Error completing interview:', error);
      toast.error('Failed to save information. Please try again.');
    }
  };

  const handleUpgradePayment = async () => {
    if (!selectedPlan) {
      toast.error('Please select a BLDR Pro plan');
      return;
    }
    
    setIsPaying(true);
    try {
      await axios.post(`${API}/interview/pay`, {
        session_id: sessionId,
        payment_type: 'evaluation',
        plan: selectedPlan.id
      });
      toast.success('Welcome to Companeze BLDR Pro! Our team will contact you shortly.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsPaying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Corporate Navy */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">Cz</span>
              </div>
              <span className="font-bold text-gray-900">Interview Lite</span>
              <Badge className="ml-2 bg-blue-900 text-white text-xs">$99</Badge>
            </div>
            <a 
              href="tel:5104224451" 
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900 transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="hidden sm:inline">(510) 422-4451</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {!interviewPaid ? (
          <Card className="max-w-xl mx-auto border border-gray-200 shadow-xl bg-white overflow-hidden">
            <div className="h-1 bg-blue-900"></div>
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <Badge className="mb-3 bg-blue-100 text-blue-900">Mini Assessment</Badge>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Interview Lite
                </h1>
                <p className="text-gray-600">
                  Quick business assessment with personalized recommendations (~15 min)
                </p>
              </div>
              
              {/* Price Box */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-gray-700 font-medium">Interview Lite</span>
                    <p className="text-xs text-blue-700">One-time assessment</p>
                  </div>
                  <span className="text-2xl font-bold text-blue-900">$99.00</span>
                </div>
              </div>

              {/* What's Included */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-3">What's included:</p>
                <ul className="space-y-2">
                  {['AI-guided business assessment', 'Personalized recommendations', 'BLDR Pro plan suggestions', 'Email summary report'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-5 h-5 rounded-full bg-blue-900 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Payment Button - Redirects to Square */}
              <Button 
                size="lg" 
                className="w-full bg-blue-900 hover:bg-blue-800 text-white text-lg py-6 rounded-lg mb-4"
                onClick={() => window.location.href = BLDR_URL}
              >
                Pay $99 via Square Checkout
              </Button>

              <p className="text-center text-sm text-gray-500 mb-4">
                Secure payment powered by Square
              </p>

              <p className="text-center text-sm text-gray-500 mb-4">
                Or call us: <a href="tel:5104224451" className="text-blue-900 font-medium hover:underline">(510) 422-4451</a>
              </p>

              {/* Demo Mode Button */}
              <div className="border-t border-gray-200 pt-4">
                <Button 
                  variant="outline"
                  className="w-full border-dashed border-gray-300 text-gray-500 hover:text-blue-900 hover:border-blue-900"
                  onClick={() => {
                    setInterviewPaid(true);
                    toast.success('Demo mode activated - Starting Interview Lite...');
                    startInterview();
                  }}
                >
                  Skip Payment (Demo Mode)
                </Button>
                <p className="text-xs text-gray-400 text-center mt-2">For testing purposes only</p>
              </div>

              {/* Upgrade Note */}
              <div className="mt-6 p-4 bg-blue-900 rounded-lg text-center">
                <p className="text-sm text-blue-200 mb-1">Looking for full-service support?</p>
                <p className="text-white font-semibold">Upgrade to BLDR Pro starting at $199/mo</p>
              </div>
            </CardContent>
          </Card>
        ) : !sessionId ? (
          <Card className="max-w-xl mx-auto border border-gray-200 shadow-xl bg-white">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-blue-900 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-700">Starting Interview Lite...</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-200 shadow-xl bg-white h-[calc(100vh-160px)] flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 bg-blue-50">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Sarah - Business Consultant</h3>
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Online
                </p>
              </div>
              <Badge className="ml-auto bg-blue-900 text-white">Interview Lite</Badge>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-5 py-4 ${
                      message.role === 'user'
                        ? 'bg-blue-900 text-white rounded-tr-sm'
                        : 'bg-white text-gray-700 rounded-tl-sm border border-gray-200 shadow-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-900" />
                      <span className="text-gray-500">Sarah is typing...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* AI Suggestions Panel */}
            {showSuggestions && aiSuggestions.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-200 bg-amber-50">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                  <span className="text-xs text-amber-800 font-medium">AI Suggestions - Click to use:</span>
                  <button 
                    onClick={() => setShowSuggestions(false)}
                    className="ml-auto text-xs text-gray-400 hover:text-gray-600"
                  >
                    Hide
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {aiSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputMessage(prev => prev ? `${prev} ${suggestion}` : suggestion);
                        textareaRef.current?.focus();
                      }}
                      className="text-xs bg-white border border-amber-200 text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-100 transition-colors text-left"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 bg-white">
              {isComplete ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-gray-900 font-medium">Interview Complete!</p>
                  <p className="text-sm text-gray-500">Processing your assessment...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Share your thoughts here... (Press Enter to send)"
                    className="min-h-[100px] text-base bg-gray-50 border-gray-200 focus:border-blue-900 resize-none"
                    disabled={isLoading}
                  />
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={getAiSuggestions}
                        disabled={isLoading}
                        className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors"
                      >
                        <Lightbulb className="w-4 h-4" /> Get Ideas
                      </button>
                      <button
                        onClick={improveMyText}
                        disabled={!inputMessage.trim() || isImproving || isLoading}
                        className="text-sm text-blue-900 hover:text-blue-800 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isImproving ? (
                          <><RefreshCw className="w-4 h-4 animate-spin" /> Improving...</>
                        ) : (
                          <><Wand2 className="w-4 h-4" /> Improve Text</>
                        )}
                      </button>
                    </div>
                    <Button 
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || isLoading}
                      className="bg-blue-900 hover:bg-blue-800 text-white px-8"
                    >
                      <Send className="w-5 h-5 mr-2" /> Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </main>

      {/* Contact Info Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900">Interview Complete!</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mb-4">
            Please provide your contact information to receive your personalized assessment report.
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="John Smith"
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                  className="pl-10 h-12 bg-gray-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Email Address *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                  className="pl-10 h-12 bg-gray-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="(555) 123-4567"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                  className="pl-10 h-12 bg-gray-50"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button 
              className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6"
              onClick={handleContactSubmit}
            >
              Get My Assessment Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade to BLDR Pro Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900">Upgrade to Companeze BLDR Pro</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mb-4">
            Your Interview Lite assessment is complete! Upgrade to BLDR Pro for full-service business development support.
          </p>
          
          {/* BLDR Pro Plans */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {bldrPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  selectedPlan?.id === plan.id
                    ? 'border-blue-900 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {plan.popular && (
                  <Badge className="bg-amber-500 text-white text-xs mb-2 border-0">
                    <Star className="w-3 h-3 mr-1" /> Popular
                  </Badge>
                )}
                <h5 className="font-semibold text-gray-900">{plan.name}</h5>
                <p className="text-xl font-bold text-gray-900">${plan.price}<span className="text-sm text-gray-400">/mo</span></p>
                <ul className="mt-2 space-y-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="text-xs text-gray-500 flex items-center gap-1">
                      <Check className="w-3 h-3 text-blue-900" /> {f}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          {/* Pay Button */}
          <Button 
            className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg mb-4"
            onClick={handleUpgradePayment}
            disabled={isPaying || !selectedPlan}
          >
            {isPaying ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
            ) : (
              <>Upgrade to {selectedPlan?.name || 'BLDR Pro'} - ${selectedPlan?.price || 199}/mo</>
            )}
          </Button>

          {/* Skip Upgrade */}
          <button
            onClick={() => {
              setShowUpgradeModal(false);
              toast.success('Your assessment report will be sent to your email.');
              navigate('/');
            }}
            className="w-full text-center text-sm text-gray-500 hover:text-blue-900 transition-colors"
          >
            Skip for now - just send my assessment report
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIInterviewPage;
